import React, { useEffect, useState } from "react";
import { Box, Pagination, Stack } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useMutation } from "@tanstack/react-query";
import { updateSingleYearByScopeEmissions, updateSingleYearByScopeConsumption, updateSingleYearByScopeFinancials } from "api";
import { COLORS, Loading } from "components";
import { chartColors, commonStyles } from "../styles";
import { SingleYearByScopeChartProps } from "../types";

;

export const SiteTypeChartSingleYear = ({
  chartTitle,
  currentMainTab,
  subTabName,
  searchFilters,
  currentFiscalYear,
}: SingleYearByScopeChartProps) => {
  const [singleYearByScopeData, setSingleYearByScopeData] = useState<any>([]);
  const apiFn = currentMainTab === "GHG Emissions" ? updateSingleYearByScopeEmissions : currentMainTab === "Consumption" ? updateSingleYearByScopeConsumption : updateSingleYearByScopeFinancials;
  const { mutateAsync, isLoading } = useMutation(apiFn);
  const noDataAvailable = !isLoading && Object.values(singleYearByScopeData).every(value =>
    typeof value === "object" && value !== null && Object.keys(value).length === 0
  );
  const { facilityType } = singleYearByScopeData;

  const [currentPage, setcurrentPage] = useState<number>(1);
  const paginationLength: number = 4;

  const isEmissionsChart = subTabName === "location" || subTabName === "market";
  const isConsumption = currentMainTab === "Consumption";

  const data: any[] = facilityType ? Object.values(facilityType).flat() : [];
  const facilityTypeCategoriesXAxis = Array.from(new Set(data.map((entry: any) =>
    entry.facilityType
  )));

  const emissionType = isEmissionsChart ? `${subTabName}BasedEmission` : isConsumption ? "consumption" : "amount";

  const facilityTypes: any = data.reduce((acc: any, entry: any) => {
    if (!acc[entry.scope]) {
      acc[entry.scope] = [];
    }
    acc[entry.scope].push({
      ...entry,
      [emissionType]: entry[emissionType] ?? 0,
    });
    return acc;
  }, {});

  const facilityTypesData: any = Object.keys(facilityTypes).reduce((acc: any, facilityTypeKey: string) => {
    acc[facilityTypeKey] = facilityTypeCategoriesXAxis.map((entry: string) => facilityTypes[facilityTypeKey].find((item: any) => item.facilityType === entry)?.[emissionType] ?? 0);
    return acc;
  }, {});

  const totalfacilityTypesData: any = Object.values(facilityTypesData).reduce((acc: any, facilityTypeData: any) => {
    facilityTypeData.forEach((value: number, index: number) => {
      if (!acc[index]) {
        acc[index] = 0;
      }
      acc[index] += value;
    });
    return acc;
  }, []);

  const seriesData = Object.keys(facilityTypesData).map((scopeKey, index) => ({
    name: scopeKey === "scope1" ? "Scope 1" : scopeKey === "scope2" ? "Scope 2" : scopeKey === "NO_SCOPE" ? "No Scope" : scopeKey,
    data: facilityTypesData[scopeKey].length > 4 ? facilityTypesData[scopeKey].slice((currentPage * paginationLength) - paginationLength, (currentPage * paginationLength)) : facilityTypesData[scopeKey],
    color: chartColors[index % chartColors.length],
    fillOpacity: 0.3,
    lineWidth: 6,
    marker: { enabled: false },
  }));

  const isPaginationDisabled: boolean = totalfacilityTypesData.length <= 4;
  const pages = totalfacilityTypesData.length % paginationLength !== 0 ? Math.trunc(totalfacilityTypesData.length / paginationLength) + 1 : totalfacilityTypesData.length / paginationLength;

  const unitYAxis = isEmissionsChart ? "tCO2e" : data[0]?.unit ?? data[0]?.currency ?? "No Data Available";

  useEffect(() => {
    const fetchSingleYearByScopeData = async () => {
      setSingleYearByScopeData({})
      await mutateAsync({
        singleYearByScopeRequest: {
          ...searchFilters,
          fiscalYear: currentFiscalYear,
        },
        chartType: 'site-type'
      }, {
        onSuccess: (response: any) => {
          setSingleYearByScopeData(response.data);
        },
        onError: () => {
          // implement error handling
        },
      })
    };

    fetchSingleYearByScopeData();
  }, [searchFilters, currentFiscalYear, mutateAsync])

  const chartOptions = {
    chart: {
      type: "column",
      spacing: [30, 50, 20, 40],
      width: null,
    },
    title: {
      text: `${chartTitle}${noDataAvailable ? ": No Data Available" : ''}`,
      align: "left",
      margin: 40,
      style: {
        ...commonStyles,
        color: COLORS.Mainblue,
        fontSize: "20px",
      },
    },
    xAxis: {
      categories: facilityTypeCategoriesXAxis.length > 4 ? facilityTypeCategoriesXAxis.slice((currentPage * paginationLength) - paginationLength, (currentPage * paginationLength)) : facilityTypeCategoriesXAxis,
      labels: {
        style: commonStyles,
      },
      startOnTick: false,
      endOnTick: false,
    },
    yAxis: {
      title: {
        text: unitYAxis,
        style: commonStyles,
        offset: 50
      },
      labels: {
        style: commonStyles,
      },
    },
    series: [
      {
        name: "Total",
        type: "line",
        data: totalfacilityTypesData.length > 4 ? totalfacilityTypesData.slice((currentPage * paginationLength) - paginationLength, (currentPage * paginationLength)) : totalfacilityTypesData,
        color: "transparent",
        dataLabels: {
          enabled: true,
          verticalAlign: "bottom",
          style: {
            ...commonStyles,
            textOutline: "none",
          },
          formatter(this: Highcharts.TooltipFormatterContextObject) {
            return `<b>${Number.parseFloat(`${this!.y}`).toFixed(2)}</b>`;
          },
          y: -1,
        },
      },
      ...seriesData,
    ],
    plotOptions: {
      line: {
        stacking: "normal",
        dataLabels: {
          enabled: true,
          style: {
            fontWeight: "bold",
            textOutline: "",
          },
          formatter(this: Highcharts.TooltipFormatterContextObject) {
            return `<b>${Number(this!.y?.toFixed(2)).toLocaleString('en-US')}</b>`;
          },
        },
      },
      column: {
        stacking: "normal",
        dataLabels: {
          enabled: true,
          style: {
            fontWeight: "bold",
            textOutline: "",
          },
          formatter(this: Highcharts.TooltipFormatterContextObject) {
            return `<b>${Number(this!.y?.toFixed(2)).toLocaleString('en-US')}</b>`;
          },
        },
      },
    },
    legend: {
      title: {
        style: commonStyles,
      },
      align: "left",
      verticalAlign: "bottom",
      x: 20,
      margin: 30,
    },
    tooltip: {
      shared: true,
      borderRadius: 8,
      shadow: false,
      padding: 14,
      useHTML: true,
      outside: true,
      positioner(labelWidth: any, labelHeight: any, point: any) {
        return {
          x: point.plotX,
          y: point.plotY - labelHeight,
        };
      },
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        return `
              <div style="font-size: 12px; padding: 8px; display: flex; flex-direction: column; gap: 5px;">
                <strong>${this!.x}</strong>
                ${this!.points
            ?.map(
              (point) => `
                  <div style="display: flex; align-items: center; justify-content: space-between; width: 180px;">
                    <div style="display: flex; align-items: center; gap: 6px;">
                      <span style="width: 10px; height: 10px; background: ${point.series.color}; border-radius: 50%; display: inline-block;"></span>
                      <span>${point.series.name}:</span>
                    </div>
                    <span style="font-weight: bold;">${Number(point.y!.toFixed(2)).toLocaleString('en-US')} ${unitYAxis}</span>
                  </div>
                `
            ).join("")}
              </div>
            `;
      },
    },
    credits: {
      enabled: false,
    },
  };

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap: 3,
      fontFamily: "Inter",
      position: "relative",
      "& > div": {
        borderRadius: 2,
      },
    }}>
      {isLoading && <Box sx={{ height: 300, position: "absolute", zIndex: 99999, right: "50%" }}>
        <Loading />
      </Box>}
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
      />
      {isPaginationDisabled
        ? ""
        : <Box sx={{
          position: "absolute",
          bottom: 22,
          right: 50,
        }}>
          <Stack spacing={2}>
            <Pagination
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "gray",
                },
                "& .Mui-selected": {
                  fontWeight: "600",
                  color: COLORS.Mainblue,
                }
              }}
              count={pages}
              page={currentPage}
              size="small"
              onChange={(event: any, page: number) => setcurrentPage(page)} />
          </Stack>
        </Box>
      }
    </Box>
  );
};
